import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const BookNow = () => {
  const [singleFormData, setSingleFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    preferredAreas: '',
    message: '',
  });

  const [scaleFormData, setScaleFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    preferredAreas: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSingleChange = (e) => {
    const { name, value } = e.target;
    setSingleFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleScaleChange = (e) => {
    const { name, value } = e.target;
    setScaleFormData((prev) => ({ ...prev, [name]: value }));
  };

  const saveToFirestoreAndCheckout = async (formData, packageName, packagePrice, stripeUrl) => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Save to Firestore
      const docRef = await addDoc(collection(db, 'leadPurchaseRequests'), {
        // Customer Info
        companyName: formData.companyName,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        preferredAreas: formData.preferredAreas,
        notes: formData.message || '',
        
        // Package Info
        packageName: packageName,
        packagePrice: packagePrice,
        
        // Status & Timestamps
        paymentStatus: 'pending',
        createdAt: serverTimestamp(),
        
        // For tracking
        source: 'website',
        stripeCheckoutUrl: stripeUrl,
      });

      console.log('Lead request saved with ID:', docRef.id);

      // Store the request ID in localStorage so we can reference it later
      localStorage.setItem('pendingLeadRequestId', docRef.id);
      localStorage.setItem('pendingLeadRequestEmail', formData.email);

      // Redirect to Stripe checkout
      // Add client_reference_id to help match payment to request
      const stripeUrlWithRef = `${stripeUrl}?client_reference_id=${docRef.id}&prefilled_email=${encodeURIComponent(formData.email)}`;
      
      window.location.href = stripeUrlWithRef;

    } catch (error) {
      console.error('Error saving lead request:', error);
      setSubmitError('There was an error processing your request. Please try again or contact us at squeakyleads@gmail.com');
      setIsSubmitting(false);
    }
  };

  const handleSinglePurchase = async (e) => {
    e.preventDefault();
    await saveToFirestoreAndCheckout(
      singleFormData,
      'Single Walkthrough',
      200,
      'https://buy.stripe.com/7sYeVe3k39pc7epgI96EU00'
    );
  };

  const handleScalePurchase = async (e) => {
    e.preventDefault();
    await saveToFirestoreAndCheckout(
      scaleFormData,
      'The Scale Pack - 10 Leads',
      1500,
      'https://buy.stripe.com/6oU9AU5sbdFsaqB8bD6EU01'
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cleaning-blue-50/30 to-fresh-white pt-24 pb-16">
      <div className="fixed inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
      <div className="fixed top-20 left-0 w-96 h-96 bg-cleaning-blue-200 rounded-full filter blur-[150px] opacity-30 pointer-events-none" />
      <div className="fixed bottom-0 right-0 w-96 h-96 bg-action-green-200 rounded-full filter blur-[150px] opacity-30 pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-cleaning-blue-600 mb-6 transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
            <span className="inline-block px-4 py-2 bg-cleaning-blue-100 text-cleaning-blue-700 rounded-full text-sm font-semibold mb-4">
              Buy Leads
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Book Your
              <span className="gradient-text"> Walkthroughs</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose your package, fill out your information, and complete your purchase through our secure checkout.
            </p>
          </div>

          {/* Error Message */}
          {submitError && (
            <div className="max-w-2xl mx-auto mb-8 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-center">
              {submitError}
            </div>
          )}

          {/* Two Package Sections */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            
            {/* Single Walkthrough Package - $200 */}
            <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
              <div className="bg-cleaning-blue-600 text-white p-6">
                <h2 className="text-2xl font-bold mb-1">Single Walkthrough</h2>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">$200</span>
                  <span className="text-cleaning-blue-200">per appointment</span>
                </div>
              </div>
              
              <div className="p-6">
                <ul className="space-y-3 mb-6">
                  {[
                    'Pre-qualified decision maker',
                    'Confirmed appointment time',
                    'Building details provided',
                    'Square footage information',
                    'Contact information included',
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-600">
                      <svg className="w-5 h-5 text-action-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                <form onSubmit={handleSinglePurchase} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Company Name *</label>
                      <input
                        type="text"
                        name="companyName"
                        required
                        value={singleFormData.companyName}
                        onChange={handleSingleChange}
                        className="input-field text-sm"
                        placeholder="Your Cleaning Co."
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name *</label>
                      <input
                        type="text"
                        name="contactName"
                        required
                        value={singleFormData.contactName}
                        onChange={handleSingleChange}
                        className="input-field text-sm"
                        placeholder="John Smith"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={singleFormData.email}
                        onChange={handleSingleChange}
                        className="input-field text-sm"
                        placeholder="you@email.com"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={singleFormData.phone}
                        onChange={handleSingleChange}
                        className="input-field text-sm"
                        placeholder="(555) 123-4567"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Target Cities (up to 3) *</label>
                    <input
                      type="text"
                      name="preferredAreas"
                      required
                      value={singleFormData.preferredAreas}
                      onChange={handleSingleChange}
                      className="input-field text-sm"
                      placeholder="Atlanta, Marietta, Decatur"
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-slate-500 mt-1">Enter up to 3 cities where you want leads</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optional)</label>
                    <textarea
                      name="message"
                      rows={2}
                      value={singleFormData.message}
                      onChange={handleSingleChange}
                      className="input-field text-sm resize-none"
                      placeholder="Any specific requirements..."
                      disabled={isSubmitting}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-cleaning-blue-600 hover:bg-cleaning-blue-700 text-white font-bold text-lg rounded-xl shadow-lg shadow-cleaning-blue-500/30 hover:shadow-xl active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        Buy Now — $200
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </>
                    )}
                  </button>
                  <p className="text-xs text-slate-500 text-center">Secure checkout powered by Stripe</p>
                </form>
              </div>
            </div>

            {/* Scale Pack - $1,500 */}
            <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border-2 border-action-green-400 overflow-hidden relative">
              <div className="absolute top-4 right-4 px-3 py-1 bg-action-green-500 text-white text-xs font-bold rounded-full">
                BEST VALUE
              </div>
              <div className="bg-gradient-to-r from-action-green-600 to-action-green-500 text-white p-6">
                <h2 className="text-2xl font-bold mb-1">The Scale Pack</h2>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">$1,500</span>
                  <span className="text-action-green-100">/ 10 appointments</span>
                </div>
                <div className="inline-flex items-center gap-1 mt-2 px-3 py-1 bg-white/20 rounded-full text-sm font-semibold">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Save $500
                </div>
              </div>
              
              <div className="p-6">
                <ul className="space-y-3 mb-6">
                  {[
                    'Everything in Single Walkthrough',
                    '10 pre-set appointments',
                    'Priority scheduling',
                    'Dedicated account support',
                    'Flexible delivery timeline',
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-600">
                      <svg className="w-5 h-5 text-action-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                <form onSubmit={handleScalePurchase} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Company Name *</label>
                      <input
                        type="text"
                        name="companyName"
                        required
                        value={scaleFormData.companyName}
                        onChange={handleScaleChange}
                        className="input-field text-sm"
                        placeholder="Your Cleaning Co."
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name *</label>
                      <input
                        type="text"
                        name="contactName"
                        required
                        value={scaleFormData.contactName}
                        onChange={handleScaleChange}
                        className="input-field text-sm"
                        placeholder="John Smith"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={scaleFormData.email}
                        onChange={handleScaleChange}
                        className="input-field text-sm"
                        placeholder="you@email.com"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={scaleFormData.phone}
                        onChange={handleScaleChange}
                        className="input-field text-sm"
                        placeholder="(555) 123-4567"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Target Cities (up to 3) *</label>
                    <input
                      type="text"
                      name="preferredAreas"
                      required
                      value={scaleFormData.preferredAreas}
                      onChange={handleScaleChange}
                      className="input-field text-sm"
                      placeholder="Atlanta, Marietta, Decatur"
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-slate-500 mt-1">Enter up to 3 cities where you want leads</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optional)</label>
                    <textarea
                      name="message"
                      rows={2}
                      value={scaleFormData.message}
                      onChange={handleScaleChange}
                      className="input-field text-sm resize-none"
                      placeholder="Any specific requirements..."
                      disabled={isSubmitting}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-action-green-600 hover:bg-action-green-700 text-white font-bold text-lg rounded-xl shadow-lg shadow-action-green-500/30 hover:shadow-xl active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        Buy Now — $1,500
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </>
                    )}
                  </button>
                  <p className="text-xs text-slate-500 text-center">Secure checkout powered by Stripe</p>
                </form>
              </div>
            </div>
          </div>

          {/* Trust Elements */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="bg-slate-50 rounded-2xl p-6">
              <h3 className="font-bold text-slate-900 mb-4 text-center">Why Squeakyleads?</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: '✓', title: 'Pre-qualified leads', desc: 'Decision makers only' },
                  { icon: '📅', title: 'Confirmed times', desc: 'No more no-shows' },
                  { icon: '📍', title: 'Your service area', desc: 'Local appointments' },
                  { icon: '💯', title: '100% focused', desc: 'On your success' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-center md:text-left">
                    <span className="text-lg hidden md:block">{item.icon}</span>
                    <div>
                      <p className="font-medium text-slate-800 text-sm">{item.title}</p>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Terms & Refund Policy Section */}
          <div className="max-w-5xl mx-auto">
            <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 md:p-8">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                <svg className="w-6 h-6 text-cleaning-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Service Details & Terms of Agreement
              </h3>

              <ul className="space-y-4 text-slate-300">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Please allow up to <strong className="text-white">60 days</strong> for your lead to be delivered (normally less than 30 days for an order of 10 leads).</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>If appointments are not emailed by your deadline date, you will be <strong className="text-white">refunded</strong> or we will continue to look for leads on your behalf.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Appointments are set around client availability (typically <strong className="text-white">Mon–Fri before noon</strong>).</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>You are responsible for closing deals; we provide <strong className="text-white">1-on-1 coaching and courses</strong> for sales/management training.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>SqueakyLeads is <strong className="text-white">not responsible</strong> for missed appointments or notifications sent to spam.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>All leads have expressed interest in a cleaning quote.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Leads cover any commercial niche (<strong className="text-white">Medical, Retail, Law, etc.</strong>) and any service (<strong className="text-white">Strip/Wax, Deep Clean, etc.</strong>).</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-cleaning-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Invalid leads will be <strong className="text-white">replaced</strong> if reported before the deadline.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-red-300"><strong className="text-red-200">NO REFUNDS</strong> will be provided unless these specific terms are not met.</span>
                </li>
              </ul>

              <div className="mt-8 pt-6 border-t border-slate-700">
                <a
                  href="mailto:squeakyleads@gmail.com?subject=Refund%20Request%3A%20%5BInsert%20Company%20Name%5D"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-transparent border-2 border-slate-500 text-slate-300 font-semibold rounded-xl hover:border-cleaning-blue-400 hover:text-cleaning-blue-400 hover:bg-slate-700/50 active:scale-[0.98] transition-all duration-200"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Contact Support for Refund
                </a>
              </div>

              <p className="mt-6 text-xs text-slate-500 leading-relaxed">
                By submitting the form above, you acknowledge that you have read these Details and agree to the terms provided.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookNow;
